import * as React from 'react';

import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cva, VariantProps } from 'class-variance-authority';

import { cn } from '@/utils';

const switchVariants = cva(
  'focus-visible:ring-ring focus-visible:ring-offset-background peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'data-[state=checked]:bg-primary-foreground data-[state=unchecked]:bg-muted-foreground',
      },
      size: {
        default: 'h-6 w-11',
        sm: 'h-3 w-5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>, VariantProps<typeof switchVariants> {}

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, variant, size, ...props }, ref) => {
    const notchSize = size === 'sm' ? 'size-2.5' : 'size-5';
    const notchPosition =
      size === 'sm' ? 'data-[state=checked]:translate-x-[calc(.4rem)]' : 'data-[state=checked]:translate-x-5';

    return (
      <SwitchPrimitives.Root className={cn(switchVariants({ variant, size }), className)} {...props} ref={ref}>
        <SwitchPrimitives.Thumb
          className={cn(
            `bg-primary-blue data-[state=checked]:bg-primary-purple pointer-events-none block h-5 w-5 rounded-full
            shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5
            data-[state=unchecked]:translate-x-0`,
            notchSize,
            notchPosition
          )}
        />
      </SwitchPrimitives.Root>
    );
  }
);
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
